import { SharedSpinner } from '@components';
import { useTechnicians } from '@hooks';
import { Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';

interface IProps {
  style?: React.CSSProperties;
  cleanName?: boolean;
  placeholder?: string;
  mode?: 'multiple' | 'tags';
  status?: 'error' | 'warning';
  stickyHeader?: boolean;
  value?: number | number[];
  allowClear?: boolean;
  disabled?: boolean;
  disabledValues?: number[];
  defaultValue?: { label: string | undefined; value: number | undefined }[];
  onClear?: () => void;
  onSelect?: (id: number) => void;
  onDeselect?: (id: number) => void;
}

export const TechnicianSelect: React.FC<IProps> = observer(
  ({
    mode,
    style,
    placeholder = 'Technician select',
    status,
    stickyHeader,
    value,
    allowClear = true,
    defaultValue,
    disabledValues,
    disabled = false,
    onClear,
    onSelect,
    onDeselect,
  }) => {
    const { technicians: users, loading } = useTechnicians();

    const options = useMemo(() => {
      const defaultOptions =
        defaultValue?.map((item) => ({
          label: item.label,
          value: item.value,
        })) || [];

      const serviceOptions = users.map((user) => ({
        label: user.fullname,
        value: user.id,
      }));

      // Combine the options and filter duplicates
      const uniqueOptionsMap = new Map();
      [...defaultOptions, ...serviceOptions].forEach((option) => {
        uniqueOptionsMap.set(`${option.label}-${option.value}`, option);
      });

      let allOptions = Array.from(uniqueOptionsMap.values());

      // Add disabled property if value is in disabledValues, and update label if disabled
      allOptions = allOptions.map((option) => {
        const isDisabled = disabledValues?.includes(option.value);
        return {
          ...option,
          disabled: isDisabled,
          label: isDisabled ? `${option.label} - unavailable` : option.label,
        };
      });

      // Sort the options alphabetically by label
      allOptions.sort((a, b) => a.label.localeCompare(b.label));

      // Move selected values to the top
      if (value) {
        const selectedValues = Array.isArray(value) ? value : [value];
        const selectedOptions = allOptions.filter((option) =>
          selectedValues.includes(option.value)
        );
        const otherOptions = allOptions.filter((option) => !selectedValues.includes(option.value));
        return [...selectedOptions, ...otherOptions];
      }

      return allOptions;
    }, [users, defaultValue, value, disabledValues]);

    return (
      <Select
        showSearch
        allowClear={allowClear}
        status={status}
        style={style}
        mode={mode}
        placeholder={placeholder}
        onClear={onClear}
        onSelect={onSelect}
        onDeselect={onDeselect}
        filterOption={(input, option) => option?.label.toLowerCase().includes(input.toLowerCase())}
        value={value}
        options={options}
        loading={loading}
        disabled={disabled}
        getPopupContainer={(stickyHeader && ((trigger: any) => trigger.parentElement)) || undefined}
        dropdownRender={(menu) => (
          <div>
            {menu}
            {loading && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <SharedSpinner loading={loading} />
              </div>
            )}
          </div>
        )}
      />
    );
  }
);
